import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { affiliateCompaniesAPI, affiliateStatsAPI, adminAPI, updateAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Globe, Plus, Loader2, TrendingUp, TrendingDown,
  ArrowUpRight, ArrowDownRight, MousePointerClick,
  UserPlus, Wallet, CalendarDays, Briefcase,
  ChevronRight, Trash2, Pencil, Users, DollarSign, Building2,
  FileSpreadsheet, Clock, ChevronDown, ChevronUp, CheckCircle2
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';

const DATE_RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'all', label: 'All' },
];

function MetricCard({ icon: Icon, label, value, color, testId }) {
  return (
    <div className="p-4 rounded-xl border border-border/50 bg-card/50" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${color}`}><Icon className="h-4 w-4" /></div>
        <span className="text-xs text-muted-foreground font-medium">{label}</span>
      </div>
      <p className="text-xl font-heading font-bold">{value}</p>
    </div>
  );
}

export default function AffiliatePlatforms() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('this_month');
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({ name: '', description: '', assigned_users: [] });
  const [submitting, setSubmitting] = useState(false);
  const [importHistory, setImportHistory] = useState([]);
  const [expandedJob, setExpandedJob] = useState(null);
  const [showPrevious, setShowPrevious] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [companiesRes, summaryRes] = await Promise.all([affiliateCompaniesAPI.getAll(), affiliateStatsAPI.getSummary(dateRange)]);
      setCompanies(companiesRes.data); setSummary(summaryRes.data);
      if (isAdmin) { try { const uRes = await adminAPI.getUsers(); setAllUsers(uRes.data.filter(u => u.role !== 'admin')); } catch {} }
      try { const hRes = await updateAPI.getImportHistory(); setImportHistory(hRes.data); } catch {}
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [dateRange, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCompanySubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      if (editingCompany) { await affiliateCompaniesAPI.update(editingCompany.id, companyForm); toast.success('Company updated'); }
      else { await affiliateCompaniesAPI.create(companyForm); toast.success('Company added'); }
      setCompanyDialogOpen(false); setCompanyForm({ name: '', description: '', assigned_users: [] }); setEditingCompany(null); fetchData(); window.dispatchEvent(new Event('affiliate-companies-changed'));
    } catch { toast.error('Operation failed'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteCompany = async (id) => { try { await affiliateCompaniesAPI.delete(id); toast.success('Company deleted'); fetchData(); window.dispatchEvent(new Event('affiliate-companies-changed')); } catch { toast.error('Delete failed'); } };
  const handleEditCompany = (company) => { setEditingCompany(company); setCompanyForm({ name: company.name, description: company.description || '', assigned_users: company.assigned_users || [] }); setCompanyDialogOpen(true); };

  const s = summary?.summary || {};
  const byBrand = summary?.by_brand || [];

  // Calculate per-company commission totals from by_brand data
  const companyCommissions = {};
  byBrand.forEach(bp => {
    const cName = bp.company_name || 'Unknown';
    if (!companyCommissions[cName]) companyCommissions[cName] = 0;
    companyCommissions[cName] += (bp.total_commission || 0);
  });
  const companyCommList = Object.entries(companyCommissions)
    .map(([name, commission]) => ({ name, commission }))
    .sort((a, b) => b.commission - a.commission);
  const totalCommission = s.total_commission || companyCommList.reduce((sum, c) => sum + c.commission, 0);

  // Calculate previous import commission data
  const companyNameMap = {};
  companies.forEach(c => { companyNameMap[c.id] = c.name; });
  const prevCompanyComm = {};
  let prevTotalComm = 0;
  importHistory.forEach(job => {
    (job.matched || []).forEach(m => {
      const dep = m.deposit_amount || 0;
      const wd = m.withdrawal_amount || 0;
      const net = dep - wd;
      const comm = net > 0 ? net / 4 : 0;
      const compName = companyNameMap[m.company_id] || 'Unknown';
      if (!prevCompanyComm[compName]) prevCompanyComm[compName] = 0;
      prevCompanyComm[compName] += comm;
      prevTotalComm += comm;
    });
  });
  const prevCommList = Object.entries(prevCompanyComm)
    .map(([name, commission]) => ({ name, commission }))
    .sort((a, b) => b.commission - a.commission);

  // Pick which data to show in the green banner
  const displayCommList = showPrevious ? prevCommList : companyCommList;
  const displayTotalComm = showPrevious ? prevTotalComm : totalCommission;

  // Group by_brand by company, preserving sidebar order (companies state order)
  const companyOrder = companies.map(c => c.name);
  const groupedByCompany = {};
  byBrand.forEach(bp => {
    const cName = bp.company_name || 'Unknown';
    if (!groupedByCompany[cName]) groupedByCompany[cName] = [];
    groupedByCompany[cName].push(bp);
  });
  const sortedCompanyGroups = companyOrder
    .filter(name => groupedByCompany[name])
    .map(name => ({ name, brands: groupedByCompany[name] }));

  if (loading) { return (<Layout title="Affiliate"><div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></Layout>); }

  return (
    <Layout title="Affiliate">
      <div className="space-y-6" data-testid="affiliate-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div><h2 className="font-heading text-2xl font-bold">Affiliate Management</h2><p className="text-muted-foreground text-sm">Manage your companies and brands</p></div>
          <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
            <DialogTrigger asChild><Button onClick={() => { setEditingCompany(null); setCompanyForm({ name: '', description: '', assigned_users: [] }); }} className="rounded-full" data-testid="add-company-btn"><Plus className="h-4 w-4 mr-2" />Add Company</Button></DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-heading">{editingCompany ? 'Edit Company' : 'Add New Company'}</DialogTitle></DialogHeader>
              <form onSubmit={handleCompanySubmit} className="space-y-4">
                <div className="space-y-2"><Label>Company Name *</Label><Input value={companyForm.name} onChange={e => setCompanyForm({ ...companyForm, name: e.target.value })} placeholder="e.g. CLICK STORE" required data-testid="company-name-input" /></div>
                <div className="space-y-2"><Label>Description</Label><Textarea value={companyForm.description} onChange={e => setCompanyForm({ ...companyForm, description: e.target.value })} placeholder="Notes about the company..." data-testid="company-desc-input" /></div>
                {isAdmin && allUsers.length > 0 && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5"><Users className="h-4 w-4" />Assigned Users</Label>
                    <div className="grid grid-cols-2 gap-1.5 max-h-32 overflow-y-auto">
                      {allUsers.map(u => (
                        <div key={u.id} className="flex items-center justify-between p-1.5 rounded bg-muted/30">
                          <span className="text-xs truncate">{u.full_name}</span>
                          <Switch checked={(companyForm.assigned_users || []).includes(u.id)} onCheckedChange={() => setCompanyForm(prev => ({ ...prev, assigned_users: (prev.assigned_users || []).includes(u.id) ? prev.assigned_users.filter(id => id !== u.id) : [...(prev.assigned_users || []), u.id] }))} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setCompanyDialogOpen(false)}>Cancel</Button><Button type="submit" disabled={submitting} data-testid="company-submit-btn">{submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editingCompany ? 'Update' : 'Add'}</Button></div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Commission Total Banner */}
        <Card className={`overflow-hidden ${showPrevious ? 'border-amber-500/30 bg-gradient-to-r from-amber-950/40 to-card' : 'border-emerald-500/30 bg-gradient-to-r from-emerald-950/40 to-card'}`} data-testid="commission-total-card">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              {/* Grand Total */}
              <div className="flex items-center gap-4 min-w-[280px]">
                <div className={`h-14 w-14 rounded-2xl flex items-center justify-center shrink-0 ${showPrevious ? 'bg-amber-500/15' : 'bg-emerald-500/15'}`}>
                  <DollarSign className={`h-7 w-7 ${showPrevious ? 'text-amber-400' : 'text-emerald-400'}`} />
                </div>
                <div>
                  <p className={`text-xs font-medium uppercase tracking-wider ${showPrevious ? 'text-amber-400/80' : 'text-emerald-400/80'}`}>{showPrevious ? 'Previous Import Commission' : 'Total Commission'}</p>
                  <p className={`text-3xl font-heading font-bold ${showPrevious ? 'text-amber-400' : 'text-emerald-400'}`} data-testid="commission-total-value">
                    {formatCurrency(displayTotalComm)}
                  </p>
                </div>
              </div>

              {/* Per-Company Breakdown */}
              {displayCommList.length > 0 && (
                <div className="flex-1 border-l border-border/30 pl-6">
                  <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5">
                    <Building2 className="h-3 w-3" /> Commission by Company
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    {displayCommList.map(c => (
                      <div key={c.name} className="px-3 py-2 rounded-lg bg-muted/30 border border-border/30" data-testid={`commission-company-${c.name}`}>
                        <p className="text-[10px] text-muted-foreground truncate">{c.name}</p>
                        <p className={`text-sm font-semibold ${c.commission > 0 ? (showPrevious ? 'text-amber-400' : 'text-emerald-400') : 'text-muted-foreground'}`}>
                          {formatCurrency(c.commission)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Date Range + Summary Metrics */}
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="flex items-center gap-2 px-6 py-4 border-b border-border/50 bg-muted/30">
              <CalendarDays className="h-4 w-4 text-muted-foreground" /><span className="text-sm text-muted-foreground mr-2">Period:</span>
              {DATE_RANGES.map(dr => (<Button key={dr.value} variant={dateRange === dr.value && !showPrevious ? 'default' : 'ghost'} size="sm" className="text-xs h-7 px-3" onClick={() => { setDateRange(dr.value); setShowPrevious(false); }} data-testid={`date-range-${dr.value}`}>{dr.label}</Button>))}
              <div className="h-5 w-px bg-border/50 mx-1" />
              <Button variant={showPrevious ? 'default' : 'ghost'} size="sm" className={`text-xs h-7 px-3 ${showPrevious ? 'bg-amber-600 hover:bg-amber-700' : ''}`} onClick={() => setShowPrevious(!showPrevious)} data-testid="date-range-previous">
                <Clock className="h-3 w-3 mr-1" />Previous
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 p-6">
              <MetricCard icon={ArrowUpRight} label="Total Deposits" value={formatCurrency(s.total_deposits || 0)} color="bg-blue-500/10 text-blue-500" testId="metric-deposits" />
              <MetricCard icon={ArrowDownRight} label="Total Withdrawals" value={formatCurrency(s.total_withdrawals || 0)} color="bg-red-500/10 text-red-500" testId="metric-withdrawals" />
              <MetricCard icon={(s.total_net_result || s.total_difference || 0) >= 0 ? TrendingUp : TrendingDown} label="Net Result" value={formatCurrency(s.total_net_result != null ? s.total_net_result : (s.total_difference != null ? s.total_difference : ((s.total_deposits || 0) - (s.total_withdrawals || 0))))} color={(s.total_net_result || s.total_difference || 0) >= 0 ? 'bg-emerald-500/10 text-emerald-500' : 'bg-orange-500/10 text-orange-500'} testId="metric-net" />
              <MetricCard icon={MousePointerClick} label="Clicks" value={(s.total_clicks || 0).toLocaleString('en-US')} color="bg-purple-500/10 text-purple-500" testId="metric-clicks" />
              <MetricCard icon={UserPlus} label="Signups" value={(s.total_signups || 0).toLocaleString('en-US')} color="bg-cyan-500/10 text-cyan-500" testId="metric-signups" />
              <MetricCard icon={Wallet} label="FTD" value={(s.total_ftd || 0).toLocaleString('en-US')} color="bg-amber-500/10 text-amber-500" testId="metric-ftd" />
            </div>
          </CardContent>
        </Card>

        {/* Brand Breakdown - Grouped by Company */}
        {sortedCompanyGroups.length > 0 && (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Company / Brand Summary</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Company</TableHead><TableHead>Brand</TableHead><TableHead className="text-right">Deposits</TableHead><TableHead className="text-right">Withdrawals</TableHead><TableHead className="text-right">Difference</TableHead><TableHead className="text-right">Clicks</TableHead><TableHead className="text-right">Signups</TableHead><TableHead className="text-right">FTD</TableHead><TableHead className="text-right">Commission</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {sortedCompanyGroups.map((group) => (
                      <React.Fragment key={group.name}>
                        {group.brands.map((bp, idx) => {
                          const isFtdZero = (bp.total_ftd || 0) === 0;
                          return (
                          <TableRow key={bp._id} className={`${idx === 0 ? 'border-t-2 border-border' : ''} ${isFtdZero ? 'bg-red-900/40 border-l-4 border-l-red-500' : ''}`}>
                            <TableCell className={`text-xs ${idx === 0 ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{idx === 0 ? group.name : ''}</TableCell>
                            <TableCell className="font-medium">{bp.brand_name}{bp.provider && <Badge variant="outline" className="ml-2 text-[10px]">{bp.provider.toUpperCase()}</Badge>}</TableCell>
                            <TableCell className="text-right text-blue-500">{formatCurrency(bp.total_deposits, bp.currency || 'TRY')}</TableCell>
                            <TableCell className="text-right text-red-500">{formatCurrency(bp.total_withdrawals, bp.currency || 'TRY')}</TableCell>
                            <TableCell className={`text-right font-medium ${(bp.total_net_result != null ? bp.total_net_result : (bp.total_deposits - bp.total_withdrawals)) >= 0 ? 'text-emerald-500' : 'text-orange-500'}`}>{formatCurrency(bp.total_net_result != null ? bp.total_net_result : (bp.total_deposits - bp.total_withdrawals), bp.currency || 'TRY')}</TableCell>
                            <TableCell className="text-right">{(bp.total_clicks || 0).toLocaleString('en-US')}</TableCell>
                            <TableCell className="text-right">{(bp.total_signups || 0).toLocaleString('en-US')}</TableCell>
                            <TableCell className="text-right">{isFtdZero ? <span className="inline-block px-2 py-0.5 rounded bg-red-500 text-white font-bold text-xs">0</span> : (bp.total_ftd || 0).toLocaleString('en-US')}</TableCell>
                            <TableCell className="text-right font-medium text-emerald-500">{formatCurrency(bp.total_commission, bp.currency || 'TRY')}</TableCell>
                          </TableRow>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Import History */}
        {importHistory.length > 0 && (
          <Card data-testid="import-history-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-primary" /> Import History
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {importHistory.map(job => {
                const isExpanded = expandedJob === job.id;
                const jobDate = job.created_at ? new Date(job.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                return (
                  <div key={job.id} className="border border-border/50 rounded-lg overflow-hidden" data-testid={`import-job-${job.id}`}>
                    <button
                      onClick={() => setExpandedJob(isExpanded ? null : job.id)}
                      className="w-full flex items-center justify-between p-3 hover:bg-muted/30 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                        <div>
                          <span className="text-sm font-medium">{job.brand_name || 'Unknown'}</span>
                          <Badge variant="outline" className="ml-2 text-[9px]">{job.template_used || job.provider?.toUpperCase()}</Badge>
                          <span className="text-[11px] text-muted-foreground ml-2">{job.file_name}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{jobDate}</span>
                        <Badge variant="secondary" className="text-[10px]">{job.matched_count} matched</Badge>
                        {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                      </div>
                    </button>
                    {isExpanded && job.matched?.length > 0 && (() => {
                      // Calculate commission summary from this import's matched data
                      const companyNameMap = {};
                      companies.forEach(c => { companyNameMap[c.id] = c.name; });
                      const jobCompanyComm = {};
                      let jobTotalComm = 0;
                      job.matched.forEach(m => {
                        const dep = m.deposit_amount || 0;
                        const wd = m.withdrawal_amount || 0;
                        const net = dep - wd;
                        const comm = net > 0 ? net / 4 : 0;
                        const compName = companyNameMap[m.company_id] || 'Unknown';
                        if (!jobCompanyComm[compName]) jobCompanyComm[compName] = 0;
                        jobCompanyComm[compName] += comm;
                        jobTotalComm += comm;
                      });
                      const jobCommList = Object.entries(jobCompanyComm)
                        .map(([name, commission]) => ({ name, commission }))
                        .sort((a, b) => b.commission - a.commission);

                      return (
                      <div className="border-t border-border/50 bg-muted/10">
                        {/* Commission Summary Banner */}
                        <div className="p-4 bg-gradient-to-r from-emerald-950/40 to-transparent border-b border-border/30">
                          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                            <div className="flex items-center gap-3 min-w-[220px]">
                              <div className="h-11 w-11 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
                                <DollarSign className="h-5 w-5 text-emerald-400" />
                              </div>
                              <div>
                                <p className="text-[10px] text-emerald-400/80 font-medium uppercase tracking-wider">Report Commission</p>
                                <p className="text-2xl font-heading font-bold text-emerald-400">{formatCurrency(jobTotalComm)}</p>
                              </div>
                            </div>
                            {jobCommList.length > 0 && (
                              <div className="flex-1 lg:border-l border-border/30 lg:pl-4">
                                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1.5">
                                  {jobCommList.map(c => (
                                    <div key={c.name} className="px-2 py-1.5 rounded bg-muted/30 border border-border/30">
                                      <p className="text-[9px] text-muted-foreground truncate">{c.name}</p>
                                      <p className={`text-xs font-semibold ${c.commission > 0 ? 'text-emerald-400' : 'text-muted-foreground'}`}>{formatCurrency(c.commission)}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Detail Table */}
                        <div className="p-3 overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="text-xs">Entity ID</TableHead>
                                <TableHead className="text-xs">Brand</TableHead>
                                <TableHead className="text-xs text-right">Clicks</TableHead>
                                <TableHead className="text-xs text-right">Regs</TableHead>
                                <TableHead className="text-xs text-right">FTD</TableHead>
                                <TableHead className="text-xs text-right">Deposit</TableHead>
                                <TableHead className="text-xs text-right">Withdrawal</TableHead>
                                <TableHead className="text-xs text-right">Net</TableHead>
                                <TableHead className="text-xs text-right">Commission</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {job.matched.map((m, i) => {
                                const dep = m.deposit_amount || 0;
                                const wd = m.withdrawal_amount || 0;
                                const net = dep - wd;
                                const comm = net > 0 ? net / 4 : 0;
                                return (
                                  <TableRow key={i}>
                                    <TableCell className="font-mono text-xs">{m.entity_id}</TableCell>
                                    <TableCell className="text-xs font-medium">{m.brand}</TableCell>
                                    <TableCell className="text-right text-xs">{(m.clicks || 0).toLocaleString()}</TableCell>
                                    <TableCell className="text-right text-xs">{(m.registrations || 0).toLocaleString()}</TableCell>
                                    <TableCell className="text-right text-xs">{(m.ftd_count || 0).toLocaleString()}</TableCell>
                                    <TableCell className="text-right text-xs text-blue-500">{formatCurrency(m.deposit_amount || 0)}</TableCell>
                                    <TableCell className="text-right text-xs text-red-500">{formatCurrency(m.withdrawal_amount || 0)}</TableCell>
                                    <TableCell className={`text-right text-xs font-medium ${net >= 0 ? 'text-emerald-500' : 'text-orange-500'}`}>{formatCurrency(net)}</TableCell>
                                    <TableCell className="text-right text-xs font-medium text-emerald-500">{formatCurrency(comm)}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                      );
                    })()}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Companies */}
        {companies.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {companies.map(company => (
              <Card key={company.id} className="card-hover cursor-pointer group" data-testid={`company-card-${company.id}`} onClick={() => navigate(`/affiliate/company/${company.id}`)}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3"><div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center"><Briefcase className="h-6 w-6 text-primary" /></div><div><h3 className="font-heading font-semibold">{company.name}</h3><p className="text-xs text-muted-foreground mt-1">{company.brand_count || 0} brands</p></div></div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  {company.description && (<p className="text-sm text-muted-foreground mt-3 line-clamp-2">{company.description}</p>)}
                  <div className="flex gap-2 mt-4 pt-4 border-t border-border" onClick={e => e.stopPropagation()}>
                    <Button variant="outline" size="sm" onClick={() => handleEditCompany(company)} data-testid={`edit-company-${company.id}`}><Pencil className="h-4 w-4 mr-1" />Edit</Button>
                    <AlertDialog><AlertDialogTrigger asChild><Button variant="outline" size="sm" className="text-destructive hover:text-destructive" data-testid={`delete-company-${company.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Company</AlertDialogTitle><AlertDialogDescription>Are you sure you want to delete "{company.name}" and all its brands?</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteCompany(company.id)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12"><div className="flex flex-col items-center justify-center text-center"><div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4"><Briefcase className="h-8 w-8 text-primary" /></div><h3 className="font-heading text-lg font-semibold mb-2">No companies added yet</h3><p className="text-muted-foreground mb-4">Get started by adding your affiliate companies</p><Button onClick={() => setCompanyDialogOpen(true)} className="rounded-full" data-testid="add-company-empty-btn"><Plus className="h-4 w-4 mr-2" />Add Company</Button></div></Card>
        )}
      </div>
    </Layout>
  );
}
